/* ==========================================================================
   Image-Video-Tabs
   Custom Element, kein Framework. Initialisiert sich selbst bei jedem
   Section-Rendering (auch im Theme-Editor).
   ========================================================================== */

class ImageVideoTabs extends HTMLElement {
  constructor() {
    super();
    this.index = 0;
    this.timer = null;
    this.onResize = this.updatePanelHeights.bind(this);
    this.onBlockSelect = this.handleBlockSelect.bind(this);
    this.onBlockDeselect = this.handleBlockDeselect.bind(this);
  }

  connectedCallback() {
    this.toggles = Array.from(this.querySelectorAll('[data-ivt-toggle]'));
    if (!this.toggles.length) return;

    this.items = Array.from(this.querySelectorAll('[data-ivt-item]'));
    this.panels = Array.from(this.querySelectorAll('[data-ivt-panel]'));
    this.slides = Array.from(this.querySelectorAll('[data-ivt-slide]'));

    this.reducedMotion =
      window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.bindEvents();
    this.select(0);
    this.startAutorotate();
  }

  disconnectedCallback() {
    this.stopAutorotate();
    window.removeEventListener('resize', this.onResize);
    document.removeEventListener('shopify:block:select', this.onBlockSelect);
    document.removeEventListener('shopify:block:deselect', this.onBlockDeselect);
    if (this.resizeObserver) this.resizeObserver.disconnect();
  }

  /* ---------------------------------------------------------- Events --- */

  bindEvents() {
    this.toggles.forEach((toggle) => {
      toggle.addEventListener('click', () => {
        this.select(Number(toggle.dataset.index));
        this.restartAutorotate();
      });

      toggle.addEventListener('keydown', (event) => {
        const last = this.toggles.length - 1;
        let next = null;

        if (event.key === 'ArrowDown' || event.key === 'ArrowRight') {
          next = this.index === last ? 0 : this.index + 1;
        } else if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') {
          next = this.index === 0 ? last : this.index - 1;
        } else if (event.key === 'Home') {
          next = 0;
        } else if (event.key === 'End') {
          next = last;
        }

        if (next === null) return;
        event.preventDefault();
        this.select(next);
        this.toggles[next].focus();
        this.restartAutorotate();
      });
    });

    this.addEventListener('click', (event) => {
      const trigger = event.target.closest && event.target.closest('[data-ivt-play]');
      if (!trigger || !this.contains(trigger)) return;
      this.loadVideo(trigger);
    });

    this.addEventListener('pointerenter', () => this.stopAutorotate());
    this.addEventListener('pointerleave', () => this.startAutorotate());
    this.addEventListener('focusin', () => this.stopAutorotate());
    this.addEventListener('focusout', (event) => {
      if (!this.contains(event.relatedTarget)) this.startAutorotate();
    });

    window.addEventListener('resize', this.onResize);
    document.addEventListener('shopify:block:select', this.onBlockSelect);
    document.addEventListener('shopify:block:deselect', this.onBlockDeselect);

    // Panelhöhe nachziehen, sobald Bilder oder Videos geladen sind
    if ('ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver(() => this.updatePanelHeights());
      this.panels.forEach((panel) => {
        const inner = panel.firstElementChild;
        if (inner) this.resizeObserver.observe(inner);
      });
    }

    this.querySelectorAll('img').forEach((img) => {
      img.addEventListener('load', () => this.updatePanelHeights());
    });
  }

  /* -------------------------------------------------------- Auswahl ---- */

  select(index) {
    if (typeof index !== 'number' || Number.isNaN(index)) return;
    this.index = Math.max(0, Math.min(index, this.toggles.length - 1));

    this.items.forEach((item, i) => item.classList.toggle('is-active', i === this.index));
    this.toggles.forEach((toggle, i) =>
      toggle.setAttribute('aria-expanded', i === this.index ? 'true' : 'false')
    );
    this.slides.forEach((slide, i) => slide.classList.toggle('is-active', i === this.index));

    this.updatePanelHeights();
    this.syncVideos();
  }

  updatePanelHeights() {
    this.panels.forEach((panel, i) => {
      const inner = panel.firstElementChild;
      const height = inner ? inner.scrollHeight : panel.scrollHeight;
      panel.style.maxHeight = i === this.index ? `${height}px` : '0px';
    });
  }

  /**
   * Tauscht das Coverbild gegen das Video aus. Erst hier entstehen die
   * Netzwerk-Requests, vorher liegt das Markup ungenutzt im <template>.
   */
  loadVideo(trigger) {
    const facade = trigger.closest('[data-ivt-facade]');
    if (!facade) return;

    const template = facade.querySelector('template[data-ivt-video]');
    if (!template) return;

    const fragment = template.content.cloneNode(true);
    const video = fragment.querySelector('video');

    facade.replaceWith(fragment);

    // Automatisches Weiterschalten würde ein laufendes Video unterbrechen
    this.autorotateLocked = true;
    this.stopAutorotate();

    if (!video) return;

    video.addEventListener('loadedmetadata', () => this.updatePanelHeights(), { once: true });
    const promise = video.play();
    if (promise && typeof promise.catch === 'function') promise.catch(() => {});

    this.updatePanelHeights();
  }

  syncVideos() {
    this.querySelectorAll('video').forEach((video) => {
      const host = video.closest('[data-ivt-slide], [data-ivt-item]');
      const active = host && host.classList.contains('is-active');

      if (active) {
        if (video.autoplay && video.paused) {
          const promise = video.play();
          if (promise && typeof promise.catch === 'function') promise.catch(() => {});
        }
      } else if (!video.paused) {
        video.pause();
      }
    });
  }

  /* ---------------------------------------------------- Autorotation --- */

  startAutorotate() {
    if (this.dataset.autorotate !== 'true') return;
    if (this.autorotateLocked) return;
    if (this.reducedMotion) return;
    if (this.toggles.length < 2) return;
    if (this.timer) return;

    const interval = Number(this.dataset.interval) || 6000;
    this.timer = window.setInterval(() => {
      this.select(this.index === this.toggles.length - 1 ? 0 : this.index + 1);
    }, interval);
  }

  stopAutorotate() {
    if (!this.timer) return;
    window.clearInterval(this.timer);
    this.timer = null;
  }

  restartAutorotate() {
    this.stopAutorotate();
    this.startAutorotate();
  }

  /* -------------------------------------------------- Theme-Editor ----- */

  handleBlockSelect(event) {
    if (!this.contains(event.target)) return;
    const item = event.target.closest('[data-ivt-item]');
    if (!item) return;
    this.stopAutorotate();
    this.select(Number(item.dataset.index));
  }

  handleBlockDeselect(event) {
    if (!this.contains(event.target)) return;
    this.startAutorotate();
  }
}

if (!customElements.get('image-video-tabs')) {
  customElements.define('image-video-tabs', ImageVideoTabs);
}